import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { TournamentCategoryEditorItem } from "./TournamentCategoriesEditor";

type AdminCreateTournamentCategoriesSectionProps = {
  activeTournamentCategoryKey: string;
  onAddCategory: () => void;
  onEditCategory: (categoryKey: string) => void;
  onRemoveCategory: (categoryKey: string) => void;
  onSelectCategory: (categoryKey: string) => void;
  hideHeader?: boolean;
  tournamentCategories: TournamentCategoryEditorItem[];
  tournamentTotalSlots: number;
  variant?: "tournament" | "challenge" | "poco";
};

export default function AdminCreateTournamentCategoriesSection({
  activeTournamentCategoryKey,
  onAddCategory,
  onEditCategory,
  onRemoveCategory,
  onSelectCategory,
  hideHeader = false,
  tournamentCategories,
  tournamentTotalSlots,
  variant = "tournament",
}: AdminCreateTournamentCategoriesSectionProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;
  const info = siteTexts.common.info;
  const isChallengeVariant = variant === "challenge" || variant === "poco";
  return (
    <>
      {!hideHeader && (
        <div className="admin-config-section-marker">
          <h4>
            <span className="info-label">
              {isChallengeVariant
                ? createTexts.challengeCategoriesTitle
                : createTexts.tournamentCategoriesTitle}
              <InfoTooltip content={info.tournamentCategories} />
            </span>
          </h4>
          <p>
            {isChallengeVariant
              ? createTexts.challengeCategoriesDescription
              : createTexts.tournamentCategoriesDescription}
          </p>
        </div>
      )}
      <div className="admin-tournament-total-slots" aria-live="polite">
        <span className="info-label">
          {createTexts.totalSlots}
          <InfoTooltip content={info.slotLimit} />
        </span>
        <strong>{tournamentTotalSlots}</strong>
        <p>{createTexts.totalSlotsDescription}</p>
      </div>
      <div className="admin-create-tournament-categories">
        <div className="status-filter-buttons admin-category-tab-actions">
          {tournamentCategories.map((category) => {
            const key = String(category.categoryKey || "").trim();
            const limit = Math.max(
              1,
              Math.floor(Number(category.slotLimit || 1)),
            );
            if (!key) return null;
            const registrationType =
              category.registrationType === "individual"
                ? "individual"
                : "fixed_pair";
            const modeLabel =
              registrationType === "fixed_pair"
                ? t.tournamentCategoryEditor.fixedPair
                : t.tournamentCategoryEditor.individual;
            return (
              <Button
                key={`create-tournament-category-tab-${key}`}
                type="button"
                variant="pill"
                className={activeTournamentCategoryKey === key ? "active" : ""}
                onClick={() => onSelectCategory(key)}
              >
                {String(
                  category.categoryName ||
                    t.tournamentCategoryEditor.fallbackCategory,
                ).trim()}{" "}
                ({modeLabel}, 0/{limit})
              </Button>
            );
          })}
          <Button
            type="button"
            variant="pill"
            className="admin-category-add-button"
            onClick={onAddCategory}
            aria-label={t.tournamentCategoryEditor.addCategory}
            title={t.tournamentCategoryEditor.addCategory}
          >
            +
          </Button>
        </div>
        <div className="admin-active-category-actions">
          <Button
            type="button"
            variant="primary"
            className="admin-category-edit-selected-button"
            disabled={!activeTournamentCategoryKey}
            onClick={() => onEditCategory(activeTournamentCategoryKey)}
          >
            {t.tournamentCategoryEditor.editSelectedCategory}
          </Button>
          <Button
            type="button"
            variant="danger"
            className="admin-category-remove-button"
            disabled={!activeTournamentCategoryKey}
            onClick={() => onRemoveCategory(activeTournamentCategoryKey)}
          >
            {t.tournamentCategoryEditor.removeCategory}
          </Button>
        </div>
      </div>
    </>
  );
}
